// Payment-request endpoints (MyPaymentRequestController — every action is a JWT POST with a
// JSON body) plus the shared status metadata used by badges and the filter chips.

import { apiFetch, apiFetchBlob, apiUploadFileWithProgress } from "@/lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PaymentRequestRow {
  id: number;
  receiptNumber: string | null;
  reqDate: string | null;
  reqNote: string | null;
  /** Request type option ({id, label}) — internal transfers get a tag on the list. */
  reqType?: { id?: number | string | null; label?: string | null } | null;
  vendorName: string | null;
  priority: string | null;
  amountReq: number | null;
  amountPaid: number | null;
  amountBalance: number | null;
  numberOfAttachedFiles: number | null;
  /** Comma-joined names across the request's amount lines (backend formula fields). */
  departmentNames?: string | null;
  expenseBudgetNames?: string | null;
  /** FOR_APPROVAL scope only: a line assigned to me is still pending. */
  pendingMyApproval?: boolean | null;
  status: string | null;
}

export interface MyRequestsPage {
  rows: PaymentRequestRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

export interface PaymentRequestAttachment {
  id: number;
  filePath: string | null;
}

export interface PaymentRequestAmountLine {
  id: number;
  amount: number | null;
  remarks: string | null;
  tag?: string | null;
  department?: { id?: number | null; name?: string | null } | null;
  expenseBudget?: {
    id?: number | null;
    name?: string | null;
    departmentName?: string | null;
    availableAmount?: number | null;
  } | null;
  /** true = approved, false = DECLINED, null/undefined = pending. */
  reqApproved?: boolean | null;
  reqApprovedBy?: { id?: number | null; displayNameWithTitle?: string | null } | null;
  reqApprovedDate?: string | null;
  reqApprovedNote?: string | null;
  /** Server-computed: the signed-in user may approve / undo-approve this line. */
  btnApprove?: boolean | null;
  btnRevertApproval?: boolean | null;
  listPaymentRequestAttachment?: PaymentRequestAttachment[] | null;
}

export interface PaymentRequestDetail {
  id: number;
  receiptNumber: string | null;
  reqDate: string | null;
  reqNote: string | null;
  vendor?: { id?: number | null; name?: string | null; pan?: string | null } | null;
  vendorPan?: string | null;
  /** Selected vendor bank account (PaymentRequestDtoService.vendorBankAccountRefAsMap). */
  vendorBankAccountRef?: {
    id?: number | null;
    bank?: { id?: number | null; name?: string | null } | null;
    bankAccount?: string | null;
    bankBranch?: string | null;
    ifscCode?: string | null;
  } | null;
  disposalInstruction?: string | null;
  priority?: { id?: number | null; label?: string | null } | null;
  legalEntity?: { id?: number | null; name?: string | null } | null;
  modeOfPayment?: { id?: number | null; label?: string | null } | null;
  operationUnit?: { id?: number | null; name?: string | null } | null;
  /** Request type option ({id, label}) — id "542" (as a number here) = internal transfer. */
  reqType?: { id?: number | string | null; label?: string | null } | null;
  /** Source budget of an internal transfer (PaymentRequestDtoService.paymentRequestTransferFromAsMap). */
  paymentRequestTransferFrom?: {
    id?: number | null;
    remarks?: string | null;
    amount?: number | null;
    department?: { id?: number | null; name?: string | null } | null;
    expenseBudget?: {
      id?: number | null;
      name?: string | null;
      departmentName?: string | null;
      availableAmount?: number | null;
    } | null;
  } | null;
  amountReq: number | null;
  amountPaid: number | null;
  amountBalance: number | null;
  status: string | null;
  accepted?: boolean | null;
  acceptedDate?: string | null;
  onHold?: boolean | null;
  onHoldReason?: string | null;
  paid?: boolean | null;
  paidDate?: string | null;
  listPaymentRequestAmount?: PaymentRequestAmountLine[] | null;
  btnEdit?: boolean | null;
  btnDelete?: boolean | null;
}

export interface NamedItem {
  id: number;
  name?: string | null;
  label?: string | null;
}

export interface MyDepartment extends NamedItem {
  operationUnitId?: number | null;
  operationUnitName?: string | null;
}

export interface PayReqInitData {
  priorities: NamedItem[];
  modeOfPayments: NamedItem[];
  expensesTypes: NamedItem[];
  myDepartments: MyDepartment[];
  legalEntities: NamedItem[];
  /** The operation units this user belongs to ({id, name} — aclService.listMyOperationUnits). */
  myOperationUnits: NamedItem[];
  /** All banks ({id, name} — accountService.listBanks), for the vendor bank-account form. */
  banks: NamedItem[];
  budgetYearId: number | null;
  reqTypePaymentRequestId: string | null;
  /** MstOption id of the "Internal transfer" request type (string, e.g. "542"). */
  reqTypeInternalTransferId: string | null;
  /** ACL: may raise regular (vendor payment) requests. */
  canMakeRegularRequest: boolean;
  /** ACL: may raise internal budget-transfer requests. */
  canMakeInternalTransfer: boolean;
  /** ACL: may approve payment requests (shows the For-approval tab + line actions). */
  canApproveRequests: boolean;
  /**
   * Mode-of-payment ids that pay into a bank account (Cheque/NEFT/RTGS) — the form shows
   * vendor bank details for these. Server sends string ids while modeOfPayments option ids
   * are numbers, so always compare with String() coercion.
   */
  bankModeOfPaymentIds: string[];
}

// ---------------------------------------------------------------------------
// List filters (drawer)
// ---------------------------------------------------------------------------

/**
 * Advanced filters applied from the list page's bottom drawer. Dates are "yyyy-MM-dd"
 * strings (dateTo inclusive server-side); ids/amounts are sent as numbers. Every field is
 * optional — only set keys are put on the apiMyRequests body.
 */
export interface MyRequestFilters {
  dateFrom?: string | null;
  dateTo?: string | null;
  vendorId?: number | null;
  /** MstOption id of the request type (541 vendor payment / 542 internal transfer). */
  reqTypeId?: number | null;
  financialYearId?: number | null;
  budgetItemId?: number | null;
  amountMin?: number | null;
  amountMax?: number | null;
  requesterDevoteeId?: number | null;
  approverDevoteeId?: number | null;
}

export const EMPTY_FILTERS: MyRequestFilters = {};

/** How many drawer filters are active (date range counts as 1, amount range as 1). */
export function countActiveFilters(f: MyRequestFilters): number {
  let n = 0;
  if (f.dateFrom || f.dateTo) n++;
  if (f.vendorId != null) n++;
  if (f.reqTypeId != null) n++;
  if (f.financialYearId != null) n++;
  if (f.budgetItemId != null) n++;
  if (f.amountMin != null || f.amountMax != null) n++;
  if (f.requesterDevoteeId != null) n++;
  if (f.approverDevoteeId != null) n++;
  return n;
}

export interface FilterOption {
  id: number;
  name: string | null;
}

/** Drawer dropdown options, scoped to the caller's own requests (financialYears newest first). */
export interface MyRequestFilterOptions {
  vendors: FilterOption[];
  financialYears: FilterOption[];
  /** name = "Head (Department)". */
  budgetItems: FilterOption[];
  requesters: FilterOption[];
  approvers: FilterOption[];
}

export async function fetchMyRequestFilterOptions(
  token: string,
  signal?: AbortSignal
): Promise<MyRequestFilterOptions> {
  const d = await apiFetch<Partial<MyRequestFilterOptions>>(
    "/api/myPaymentRequest/apiMyRequestFilterOptions",
    { token, method: "POST", body: {}, signal }
  );
  return {
    vendors: d?.vendors ?? [],
    financialYears: d?.financialYears ?? [],
    budgetItems: d?.budgetItems ?? [],
    requesters: d?.requesters ?? [],
    approvers: d?.approvers ?? [],
  };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export function fetchMyRequests(
  token: string,
  params: {
    status?: string | null;
    search?: string | null;
    scope?: "FOR_APPROVAL" | null;
    filters?: MyRequestFilters | null;
    currentPage: number;
    pageSize?: number;
  },
  signal?: AbortSignal
): Promise<MyRequestsPage> {
  const body: Record<string, unknown> = {
    currentPage: params.currentPage,
    pageSize: params.pageSize ?? 20,
  };
  if (params.scope) body.scope = params.scope;
  if (params.status) body.status = params.status;
  if (params.search?.trim()) body.search = params.search.trim();
  const f = params.filters;
  if (f) {
    if (f.dateFrom) body.dateFrom = f.dateFrom;
    if (f.dateTo) body.dateTo = f.dateTo;
    if (f.vendorId != null) body.vendorId = f.vendorId;
    if (f.reqTypeId != null) body.reqTypeId = f.reqTypeId;
    if (f.financialYearId != null) body.financialYearId = f.financialYearId;
    if (f.budgetItemId != null) body.budgetItemId = f.budgetItemId;
    if (f.amountMin != null) body.amountMin = f.amountMin;
    if (f.amountMax != null) body.amountMax = f.amountMax;
    if (f.requesterDevoteeId != null) body.requesterDevoteeId = f.requesterDevoteeId;
    if (f.approverDevoteeId != null) body.approverDevoteeId = f.approverDevoteeId;
  }
  return apiFetch<MyRequestsPage>("/api/myPaymentRequest/apiMyRequests", {
    token,
    method: "POST",
    body,
    signal,
  });
}

export async function fetchRequestDetail(
  token: string,
  id: number | string,
  signal?: AbortSignal
): Promise<PaymentRequestDetail> {
  const d = await apiFetch<{ paymentRequest?: PaymentRequestDetail; errorMessage?: string }>(
    "/api/myPaymentRequest/apiGetRequest",
    { token, method: "POST", body: { id }, signal }
  );
  if (!d?.paymentRequest) {
    throw new Error(d?.errorMessage || "Payment request not found.");
  }
  return d.paymentRequest;
}

export function fetchInitData(token: string, signal?: AbortSignal): Promise<Partial<PayReqInitData>> {
  return apiFetch<Partial<PayReqInitData>>("/api/myPaymentRequest/apiInitData", {
    token,
    method: "POST",
    body: {},
    signal,
  });
}

// ---------------------------------------------------------------------------
// Request form: cascades, vendors, save/delete, attachments
// ---------------------------------------------------------------------------

/** One requestable budget item of a department (BudgetService.listBudgetItemsRequestable). */
export interface BudgetItem {
  id: number;
  /** "Department : Budget head : Year" (shared items say "(Shared)"). */
  name: string;
  amount: number | null;
  requested: number | null;
  /** Amount returned via internal transfers (the backend field name carries this typo). */
  returnred?: number | null;
  availableAmount: number | null;
  year: string | null;
  /** 0/1/null — items NOT enabled are still returned; the form must not offer them. */
  paymentRequestEnabled: number | boolean | null;
}

export function fetchBudgetItemsOfDepartment(
  token: string,
  departmentId: number,
  signal?: AbortSignal
): Promise<BudgetItem[]> {
  return apiFetch<BudgetItem[]>("/api/myPaymentRequest/apiBudgetItemsOfDepartment", {
    token,
    method: "POST",
    body: { departmentId },
    signal,
  });
}

export interface Approver {
  id: number;
  displayNameWithTitle: string | null;
  displayNameWithTitleSearchable: string | null;
  photoUrl: string | null;
}

export interface ApproversOfBudget {
  approvers: Approver[];
  /** Plain strings (ExpenseBudget.tags elements). */
  tags: string[];
}

export function fetchApproversOfBudget(
  token: string,
  budgetItemId: number,
  signal?: AbortSignal
): Promise<ApproversOfBudget> {
  return apiFetch<ApproversOfBudget>("/api/myPaymentRequest/apiApproversOfBudget", {
    token,
    method: "POST",
    body: { budgetItemId },
    signal,
  });
}

export interface VendorOption {
  id: number;
  name: string;
  pan: string | null;
  email: string | null;
  verifiedForPayment: boolean;
}

export async function searchVendors(
  token: string,
  operationUnitId: number,
  search: string,
  signal?: AbortSignal
): Promise<VendorOption[]> {
  const d = await apiFetch<{ vendors?: VendorOption[] }>("/api/myPaymentRequest/apiVendorSearch", {
    token,
    method: "POST",
    body: search.trim() ? { operationUnitId, search: search.trim() } : { operationUnitId },
    signal,
  });
  return d?.vendors ?? [];
}

/** One bank account of a vendor (PaymentRequestDtoService.vendorBankAccountsAsMap). */
export interface VendorBankAccount {
  id: number;
  bank: { id?: number | null; name?: string | null } | null;
  bankAccount: string | null;
  bankBranch: string | null;
  ifscCode: string | null;
  accountHolderName: string | null;
  verificationProofFileName: string | null;
  bankAcVerified: boolean;
  isDefault: boolean;
  verifiedForPayment: boolean;
}

/** Bank accounts of one vendor, default-first; empty when the vendor is not in my OUs. */
export async function fetchVendorBankAccounts(
  token: string,
  vendorId: number,
  signal?: AbortSignal
): Promise<VendorBankAccount[]> {
  const d = await apiFetch<{ bankAccounts?: VendorBankAccount[] }>(
    "/api/myPaymentRequest/apiVendorBankAccounts",
    { token, method: "POST", body: { vendorId }, signal }
  );
  return d?.bankAccounts ?? [];
}

// --- Save / delete ---

export interface SaveAttachmentPayload {
  /** Existing PaymentRequestAttachment id (edit) — send it back or the server soft-deletes it. */
  id?: number;
  filePath: string;
}

export interface SaveAmountLinePayload {
  /** Existing PaymentRequestAmount id (edit) — send it back or the server soft-deletes it. */
  id?: number;
  department: { id: number };
  expenseBudget: { id: number };
  reqApprovedBy: { id: number };
  amount: number;
  remarks?: string;
  tag?: string | null;
  listPaymentRequestAttachment: SaveAttachmentPayload[];
}

export interface SaveRequestPayload {
  id?: number; // edit
  /**
   * Sent for internal transfers only ({id: reqTypeInternalTransferId}); omitted for regular
   * requests — the server normalizes a missing/unknown type to a regular request.
   */
  reqType?: { id: string };
  priority: { id: number };
  legalEntity: { id: number };
  /** Regular requests only — the server nulls it for internal transfers. */
  modeOfPayment?: { id: number };
  /** Regular requests only — the server nulls it for internal transfers. */
  vendor?: { id: number };
  /** Selected vendor bank account (bank payment modes only; server validates ownership). */
  vendorBankAccountRef?: { id: number };
  vendorPan?: string;
  disposalInstruction?: string;
  reqNote: string;
  /**
   * Internal transfers only: the source budget the total is transferred from. `id` is the
   * existing PaymentRequestTransferFrom id (edit mode only). amount is set server-side to
   * the request total.
   */
  paymentRequestTransferFrom?: { id?: number; expenseBudget: { id: number } };
  listPaymentRequestAmount: SaveAmountLinePayload[];
}

export interface SaveRequestResult {
  myMessage?: string;
  /** HTML-ish, may contain <br/> separators — render as split lines, never as HTML. */
  errors?: string;
  ACTION?: "ADD-NEW" | "UPDATE";
  ID?: number;
}

export function saveRequest(token: string, payload: SaveRequestPayload): Promise<SaveRequestResult> {
  return apiFetch<SaveRequestResult>("/api/myPaymentRequest/apiSaveRequest", {
    token,
    method: "POST",
    body: payload,
  });
}

export function deleteRequest(token: string, id: number): Promise<{ myMessage?: string }> {
  return apiFetch<{ myMessage?: string }>("/api/myPaymentRequest/apiDeleteRequest", {
    token,
    method: "POST",
    body: { id },
  });
}

// --- Attachments ---

export interface UploadAttachmentResult {
  myMessage?: string;
  errorMessage?: string;
  filePath?: string;
  /** Present only when amountId was sent (row persisted immediately). */
  attachmentId?: number;
}

/**
 * Multipart upload (field "filePath"). Pass amountId ONLY for a line that already exists
 * server-side (edit mode) — the server then persists the attachment row immediately.
 */
export function uploadAttachment(
  token: string,
  file: File,
  amountId: number | undefined,
  onProgress?: (percent: number | null) => void
): Promise<UploadAttachmentResult> {
  const path = amountId
    ? `/api/myPaymentRequest/apiUploadAttachment?amountId=${amountId}`
    : "/api/myPaymentRequest/apiUploadAttachment";
  return apiUploadFileWithProgress<UploadAttachmentResult>(path, token, file, onProgress, "filePath");
}

/** Fetch one attachment (auth header → blob) for viewing via an object URL. */
export interface ApprovalActionResult {
  myMessage?: string;
  errors?: string;
  errorMessage?: string;
  req?: PaymentRequestDetail;
}

/** Approver: approve one amount line assigned to me. */
export function approveAmount(token: string, amountId: number): Promise<ApprovalActionResult> {
  return apiFetch<ApprovalActionResult>("/api/myPaymentRequest/apiApproveAmount", {
    token,
    method: "POST",
    body: { amountId },
  });
}

/** Approver: undo my approval of one amount line. */
export function undoApproveAmount(token: string, amountId: number): Promise<ApprovalActionResult> {
  return apiFetch<ApprovalActionResult>("/api/myPaymentRequest/apiUndoApproveAmount", {
    token,
    method: "POST",
    body: { amountId },
  });
}

/** Approver: decline one pending amount line with a reason. */
export function declineAmount(
  token: string,
  amountId: number,
  reason: string
): Promise<ApprovalActionResult> {
  return apiFetch<ApprovalActionResult>("/api/myPaymentRequest/apiDeclineAmount", {
    token,
    method: "POST",
    body: { amountId, reason },
  });
}

export function downloadAttachmentBlob(token: string, attachmentId: number): Promise<Blob> {
  return apiFetchBlob(`/api/myPaymentRequest/apiDownloadAttachment?id=${attachmentId}`, token);
}

export function deleteAttachment(token: string, id: number): Promise<{ myMessage?: string }> {
  return apiFetch<{ myMessage?: string }>("/api/myPaymentRequest/apiDeleteAttachment", {
    token,
    method: "POST",
    body: { id },
  });
}

export function sendApprovalNotification(
  token: string,
  amountId: number
): Promise<{ myMessage?: string; errorMessage?: string }> {
  return apiFetch<{ myMessage?: string; errorMessage?: string }>(
    "/api/myPaymentRequest/apiSendApprovalNotification",
    { token, method: "POST", body: { amountId } }
  );
}

// ---------------------------------------------------------------------------
// Notifications (bell screen + badge)
// ---------------------------------------------------------------------------

export interface AppNotificationRow {
  id: number;
  title: string | null;
  body: string | null;
  link: string | null;
  isRead: boolean;
  dateCreated: string | null;
}

export interface MyNotificationsPage {
  rows: AppNotificationRow[];
  totalCount: number;
  unreadCount: number;
  currentPage: number;
  pageSize: number;
}

export function fetchMyNotifications(
  token: string,
  params: { currentPage: number; pageSize?: number },
  signal?: AbortSignal
): Promise<MyNotificationsPage> {
  return apiFetch<MyNotificationsPage>("/api/myPaymentRequest/apiMyNotifications", {
    token,
    method: "POST",
    body: { currentPage: params.currentPage, pageSize: params.pageSize ?? 20 },
    signal,
  });
}

export function markNotificationsRead(
  token: string,
  params: { all?: boolean; ids?: number[] }
): Promise<{ myMessage?: string }> {
  return apiFetch<{ myMessage?: string }>("/api/myPaymentRequest/apiMarkNotificationsRead", {
    token,
    method: "POST",
    body: params.all ? { all: true } : { ids: params.ids ?? [] },
  });
}

// ---------------------------------------------------------------------------
// Request type helpers
// ---------------------------------------------------------------------------

/** Fallback for stale cached init data (Constants.PAYMENT_REQUEST_TYPE_INTERNAL_TRANSFER_ID). */
export const REQ_TYPE_INTERNAL_TRANSFER_ID = "542";

/**
 * Is this reqType option the internal-transfer type? Server option ids are numbers while
 * initData's reqTypeInternalTransferId is a string — compare via String(); the label match
 * is a fallback for responses missing the id.
 */
export function isInternalTransferType(
  reqType: { id?: number | string | null; label?: string | null } | null | undefined,
  internalTransferId?: string | null
): boolean {
  if (!reqType) return false;
  if (reqType.id != null) {
    return String(reqType.id) === String(internalTransferId ?? REQ_TYPE_INTERNAL_TRANSFER_ID);
  }
  return (reqType.label ?? "").trim().toLowerCase() === "internal transfer";
}

// ---------------------------------------------------------------------------
// Status metadata
// ---------------------------------------------------------------------------

export interface StatusMeta {
  label: string;
  /** Tailwind classes applied to the Badge (light + dark). */
  className: string;
}

export const STATUS_META: Record<string, StatusMeta> = {
  PENDING_APPROVAL: {
    label: "Pending approval",
    className:
      "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200",
  },
  APPROVED: {
    label: "Approved",
    className:
      "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200",
  },
  PROCESSING: {
    label: "Processing",
    className:
      "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-200",
  },
  ON_HOLD: {
    label: "On hold",
    className:
      "border-red-300 bg-red-100 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200",
  },
  PARTIALLY_PAID: {
    label: "Partially paid",
    className:
      "border-teal-300 bg-teal-100 text-teal-900 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-200",
  },
  PAID: {
    label: "Paid",
    className:
      "border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200",
  },
};

export function statusMeta(status?: string | null): StatusMeta {
  if (status && STATUS_META[status]) return STATUS_META[status];
  return {
    label: status ? status.replace(/_/g, " ").toLowerCase() : "—",
    className: "border-border bg-muted text-foreground",
  };
}

/** Filter chips for the list page — PARTIALLY_PAID intentionally appears only as a badge. */
export const STATUS_FILTERS: { value: string | null; label: string }[] = [
  { value: null, label: "All" },
  { value: "PENDING_APPROVAL", label: "Pending approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "PROCESSING", label: "Processing" },
  { value: "ON_HOLD", label: "On hold" },
  { value: "PAID", label: "Paid" },
];

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

export function formatAmount(value?: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `₹${INR.format(value)}`;
}
